import buttonsMessageTypeAnswerOptions from '@/json/buttonsMessageTypeAnswerOptions';
import { ConvoMessageItemType } from '@/store/enums/convoMessageItemType.enum';
import { ListMarker } from '@/store/enums/listMarker.enum';
import { AccordionItemBase, Buttons, Carousel, InlineResponse, LogicJump } from '@/store/models';
import { getErrorMessage } from '@/store/services/validationService';
import {
    IsNotEmpty,
    IsUrl,
    Length,
    validate as validateClassInstance,
    ValidateIf,
    ValidationError,
} from 'class-validator';
import { v4 as uuid } from 'uuid';
import { AnswerType, ConvoTypes } from '../enums';
import { Attributes } from '../enums/attributes.enum';
import { IConvoState } from '../modules/convo-details';
import { ButtonsAnswerType } from './buttonsAnswerType';
import { IButtonAttributes, IDatumContentItemAnswer } from './interfaces';
import { IQuizOptions } from './interfaces/quizOptions.interface';

export class ButtonsAnswer
    extends AccordionItemBase
    implements IQuizOptions, IButtonAttributes, IDatumContentItemAnswer
{
    constructor(items = []) {
        super(items);
        this.type = ConvoMessageItemType.ButtonsAnswer;
        this.buttonId = uuid();
        this.buttonLabel = '';
        this.isDraggable = true;
        this.isDeletable = true;
        this.canChangeAnswerType = true;
        this.canHaveAttributes = true;
        this.markerType = ListMarker.Numeral;
        this.items = [new InlineResponse()];
        this.link = '';
        this.successDispositionLink = '';
        this.failureDispositionLink = '';
        this.linkOpenInNewTab = false;
        this.logicJump = new LogicJump();
        this.isCorrectAnswer = false;
        this.attributes = [];
        this.datumResponseCode = '';

        this.setOptions();
    }

    private setOptions() {
        this.answerTypeOptions = buttonsMessageTypeAnswerOptions.map(
            (item: any) =>
                new ButtonsAnswerType({
                    name: item.name,
                    title: item.title,
                    linkRequired: item.linkRequired,
                    canBeMultipleChoice: item.canBeMultipleChoice,
                    excludeMessageItemTypes: item.excludeMessageItemTypes,
                    excludeConvoTypes: item.excludeConvoTypes,
                }),
        );

        if (this.answerTypeOptions && this.answerTypeOptions.length > 0) {
            this.answerType = this.answerTypeOptions[0];
        }
    }

    externalId?: string;
    buttonId: string;
    @IsNotEmpty({ message: getErrorMessage('isNotEmpty', 'Button Label') })
    @Length(0, 100, { message: getErrorMessage('length', 'Button Label', { min: 0, max: 100 }) })
    buttonLabel: string;
    answerType: ButtonsAnswerType;
    answerTypeOptions: ButtonsAnswerType[];
    @IsUrl({}, { message: getErrorMessage('isUrl', 'Link') })
    @ValidateIf((o) => o.answerType.linkRequired)
    link: string;
    @IsUrl({}, { message: getErrorMessage('isUrl', 'Success Disposition Link') })
    @ValidateIf(
        (o) => o.answerType.linkRequired && o.answerType.name === AnswerType.LinkNextDisposition,
    )
    successDispositionLink: string;
    @IsUrl({}, { message: getErrorMessage('isUrl', 'Failure Disposition Link') })
    @ValidateIf(
        (o) => o.answerType.linkRequired && o.answerType.name === AnswerType.LinkNextDisposition,
    )
    failureDispositionLink: string;
    linkOpenInNewTab: boolean;
    logicJump: LogicJump;
    isCorrectAnswer: boolean;
    isExclusive?: boolean;
    selectedPersonalityQuizResult: number | undefined;
    canChangeAnswerType?: boolean;
    canHaveAttributes?: boolean;
    attributes: Attributes[];
    datumResponseCode: string;

    async validate(state: IConvoState, path: number[]): Promise<any> {
        const errors: ValidationError[] = await validateClassInstance(this);
        const errorMessages: any[] = [];

        if (errors.length > 0) {
            errors.forEach((item: ValidationError) => {
                errorMessages.push({
                    path,
                    errors: { [item.property]: Object.values(item.constraints || {}) },
                });
            });
        }

        // Inline response
        if (this.items[0].items[0]) {
            const inlineResponseErrorMessages = await this.items[0].items[0].validate(state, [
                ...path,
                0,
                0,
            ]);
            errorMessages.push(...inlineResponseErrorMessages);
        }

        // Personality quiz
        const parent = state.messagesView.messageItems[path[0]] as Buttons | Carousel;

        if (
            state.convo.type === ConvoTypes.PersonalityQuiz &&
            (this.selectedPersonalityQuizResult === undefined ||
                this.selectedPersonalityQuizResult === null) &&
            !parent.ignoreQuizQuestion
        ) {
            errorMessages.push({
                path,
                errors: {
                    selectedPersonalityQuizResult: [
                        getErrorMessage('selectOption', 'Personality Quiz Result'),
                    ],
                },
            });
        }

        return errorMessages;
    }

    clearDatumAnswer(): void {
        this.datumResponseCode = '';
    }
}
